import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import TranslateIcon from "@mui/icons-material/Translate";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "../../i18n";
import { glass } from "../../theme/glassTokens";
import api from "../../api/api";

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ca", label: "Català", flag: "🏴" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

type ConsentModule = {
  name: string;
  label: string;
  status: "required" | "recommended" | "optional";
  description: string;
};

type ConsentModulesResponse = {
  modules: ConsentModule[];
  presets: Record<string, string[]>;
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type PatientConsentGeneratorProps = {
  orgDefaults?: {
    name?: string;
    address?: string;
    nif?: string;
    dpoEmail?: string;
  };
};

export default function PatientConsentGenerator({ orgDefaults }: PatientConsentGeneratorProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const [language, setLanguage] = useState("en");
  const [institutionName, setInstitutionName] = useState(orgDefaults?.name ?? "");
  const [institutionAddress, setInstitutionAddress] = useState(orgDefaults?.address ?? "");
  const [institutionNif, setInstitutionNif] = useState(orgDefaults?.nif ?? "");
  const [dpoEmail, setDpoEmail] = useState(orgDefaults?.dpoEmail ?? "");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modulesData, setModulesData] = useState<ConsentModulesResponse | null>(null);
  const [selectedModules, setSelectedModules] = useState<string[]>(["core", "research"]);
  const [preset, setPreset] = useState<string>("standard");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await api.get<ConsentModulesResponse>("/billing/legal/consent/modules/");
        if (mounted) {
          setModulesData(resp.data);
          if (resp.data.presets.standard) {
            setSelectedModules(resp.data.presets.standard);
          }
        }
      } catch {
        // Fallback: use local defaults
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handlePresetChange = useCallback((_: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (!value || !modulesData?.presets[value]) return;
    setPreset(value);
    setSelectedModules(modulesData.presets[value]);
  }, [modulesData]);

  const handleModuleToggle = useCallback((moduleName: string, checked: boolean) => {
    setSelectedModules((prev) => {
      const next = checked ? [...prev, moduleName] : prev.filter((m) => m !== moduleName);
      if (modulesData) {
        const matchingPreset = Object.entries(modulesData.presets).find(
          ([, modules]) => modules.length === next.length && modules.every((m) => next.includes(m)),
        );
        setPreset(matchingPreset ? matchingPreset[0] : "custom");
      }
      return next;
    });
  }, [modulesData]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const resp = await api.post(
        "/billing/legal/consent/generate-pdf/",
        {
          language,
          institution_name: institutionName,
          institution_address: institutionAddress,
          institution_nif: institutionNif,
          institution_dpo_email: dpoEmail,
          modules: selectedModules,
        },
        { responseType: "blob" },
      );
      downloadBlob(resp.data as Blob, `patient_consent_${language}.pdf`);
    } catch {
      setError(t("legal.consent.generate_error", "Failed to generate the consent PDF. Please try again."));
    } finally {
      setGenerating(false);
    }
  };

  const sectionSx = {
    p: 2.5,
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.18)}`,
    background: alpha(theme.palette.background.paper, 0.55),
    backdropFilter: "blur(10px)",
  };

  const statusChipProps = (status: string) => {
    switch (status) {
      case "required":
        return { label: t("legal.consent.required", "Required"), color: "error" as const };
      case "recommended":
        return { label: t("legal.consent.recommended", "Recommended"), color: "success" as const };
      default:
        return { label: t("legal.consent.optional", "Optional"), color: "default" as const };
    }
  };

  return (
    <Box sx={sectionSx}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <TranslateIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          {t("legal.consent.title", "Patient Consent Generator")}
        </Typography>
      </Box>

      <Typography variant="body2" color={glass.textSecondary} sx={{ mb: 2 }}>
        {t(
          "legal.consent.description",
          "Generate a patient informed consent document with your institution's details pre-filled. Select which consent modules to include.",
        )}
      </Typography>

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          select
          fullWidth
          size="small"
          label={t("legal.consent.language", "Language")}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          {LANGUAGES.map((lng) => (
            <MenuItem key={lng.code} value={lng.code}>
              {lng.flag} {lng.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          size="small"
          label={t("legal.consent.institution_name", "Institution Name")}
          value={institutionName}
          onChange={(e) => setInstitutionName(e.target.value)}
        />
        <TextField
          fullWidth
          size="small"
          label={t("legal.consent.institution_address", "Registered Address")}
          value={institutionAddress}
          onChange={(e) => setInstitutionAddress(e.target.value)}
        />
        <Stack direction="row" spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            label={t("legal.consent.institution_nif", "Tax ID (NIF/CIF)")}
            value={institutionNif}
            onChange={(e) => setInstitutionNif(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label={t("legal.consent.dpo_email", "DPO / Privacy Contact Email")}
            value={dpoEmail}
            onChange={(e) => setDpoEmail(e.target.value)}
          />
        </Stack>
      </Stack>

      {/* Consent Module Selection */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 1 }}
        >
          {t("legal.consent.modules_title", "Consent Modules")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Typography variant="caption" color={glass.textSecondary} sx={{ fontWeight: 600 }}>
            {t("legal.consent.preset_label", "Preset:")}
          </Typography>
          <ToggleButtonGroup
            value={preset}
            exclusive
            onChange={handlePresetChange}
            size="small"
          >
            <ToggleButton value="standard" sx={{ textTransform: "none", fontSize: "0.72rem", fontWeight: 700, px: 1.5 }}>
              {t("legal.consent.preset_standard", "Standard")}
            </ToggleButton>
            <ToggleButton value="extended" sx={{ textTransform: "none", fontSize: "0.72rem", fontWeight: 700, px: 1.5 }}>
              {t("legal.consent.preset_extended", "Extended")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Stack spacing={0.5}>
          {(modulesData?.modules ?? [
            { name: "core", label: "Clinical Care", status: "required" as const, description: "Data processing for clinical care and AI-assisted analysis." },
            { name: "research", label: "Research & AI Improvement", status: "recommended" as const, description: "Pseudonymized research and anonymized algorithm improvement." },
            { name: "third_party", label: "Third-Party Collaboration", status: "optional" as const, description: "Facilitated data sharing with authorized third parties for clinical trials." },
          ]).map((mod) => {
            const isRequired = mod.status === "required";
            const isChecked = selectedModules.includes(mod.name);
            const chipProps = statusChipProps(mod.status);
            return (
              <Box
                key={mod.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(
                    isChecked ? theme.palette.secondary.main : theme.palette.divider,
                    isChecked ? 0.3 : 0.5,
                  )}`,
                  background: isChecked ? alpha(theme.palette.secondary.main, 0.04) : "transparent",
                  transition: "all 0.15s ease",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked}
                      disabled={isRequired}
                      onChange={(_, checked) => handleModuleToggle(mod.name, checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {mod.label}
                      </Typography>
                      <Chip
                        size="small"
                        {...chipProps}
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
                      />
                    </Box>
                  }
                  sx={{ flex: 1, m: 0 }}
                />
                <Tooltip title={mod.description} placement="top" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }} />
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {error && (
        <Typography variant="body2" color="error.main" sx={{ fontWeight: 600, mb: 1.5 }}>
          {error}
        </Typography>
      )}

      <Button
        variant="contained"
        startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
        onClick={handleGenerate}
        disabled={generating}
        sx={{ textTransform: "none", fontWeight: 800 }}
      >
        {t("legal.consent.download_pdf", "Download Consent PDF")}
      </Button>
    </Box>
  );
}
